import React, { useMemo, useRef } from 'react';
import { isNumber } from 'lodash';
import type { ApiError } from '@axiom/ui';
import {
  BadRequestError,
  Button,
  CondensedHeader,
  CondensedLabel,
  CondensedLarge,
  CondensedMedium,
  Flashy,
  Form,
  FormGroup,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  Layout,
  LayoutItem,
  Radio,
  ToastUtil,
  Tooltip,
  useApi,
  Well,
} from '@axiom/ui';
import type {
  AhqSubmission,
  Candidate,
  Opportunity,
  OpportunityCandidate,
} from '@axiom/validation';
import { CandidateOpportunityUtils } from '@axiom/utils';

import {
  getOpportunityCandidates,
  OpportunityLegacyApi,
} from '../../api/opportunity-legacy-api';
import { CandidateApi } from '../../api/candidate-api';
import { OpportunityStore } from '../../stores/opportunity-store';
import { CandidateRateUtil } from '../../utils/candidate-rate-util';
import { NumberUtil } from '../../utils/number-util';
import { PreloadedAppErrorsStore } from '../../stores/preloaded-app-errors-store';

import type {
  OpportunitySubmissionCandidateRateFieldToCalculateType,
  OpportunitySubmissionCandidateRateSchemaType,
} from './opportunity-submission-candidate-rate-schema';
import { OpportunitySubmissionCandidateRateSchema } from './opportunity-submission-candidate-rate-schema';
import { OpportunitySubmissionCandidateRateUtil } from './opportunity-submission-candidate-rate-util';
import { OpportunitySubmissionCandidateRateCpqTargets } from './OpportunitySubmissionCandidateRateCpqTargets';
import { OpportunitySubmissionCandidateRateInput } from './OpportunitySubmissionCandidateRateInput';
import { OpportunitySubmissionCandidateRateCurrencyConversion } from './OpportunitySubmissionCandidateRateCurrencyConversion';

export type OpportunitySubmissionCandidateRateProps = {
  candidateId: Candidate['id'];
  opportunityId: Opportunity['id'];
  name?: string;
  readOnly?: boolean;
  submission: AhqSubmission;
};

const refreshOpportunityCandidates = async (
  opportunityId: Opportunity['id']
) => {
  await Promise.all([
    OpportunityLegacyApi.refreshOpportunityCandidates(opportunityId),
    OpportunityLegacyApi.refreshOpportunityPositions(opportunityId),
  ]);
};

export const OpportunitySubmissionCandidateRate = ({
  candidateId,
  name = 'opportunity-submission-candidate-rate-form',
  opportunityId,
  readOnly = false,
  submission,
}: OpportunitySubmissionCandidateRateProps) => {
  const [{ data: candidate }, { data: opportunityCandidates }] = useApi(
    CandidateApi.readCandidate(candidateId),
    OpportunityLegacyApi.readOpportunityCandidates(opportunityId)
  );

  const fieldToCalculateRef =
    useRef<OpportunitySubmissionCandidateRateFieldToCalculateType>(
      'proposedHourlyBillRate'
    );

  const opportunityCandidate = opportunityCandidates.find(
    oc => oc.candidateId === candidateId
  );
  const candidateCurrency =
    OpportunitySubmissionCandidateRateUtil.getCurrencyOrDefault({
      currency: candidate.currencyType,
    });
  const submissionCurrency =
    OpportunitySubmissionCandidateRateUtil.getCurrencyOrDefault({
      currency: submission.currency,
    });
  const isConversion = OpportunitySubmissionCandidateRateUtil.isConversion({
    submissionCurrency,
    candidateCurrency,
  });

  const candidateCurrencyLabel = useMemo(
    () =>
      OpportunitySubmissionCandidateRateUtil.getCurrencyLabel({
        currency: candidateCurrency,
        isConversion,
        name: 'candidate-currency-label',
      }),
    [candidateCurrency, isConversion]
  );

  const clientCurrencyLabel = useMemo(
    () =>
      OpportunitySubmissionCandidateRateUtil.getCurrencyLabel({
        currency: submissionCurrency,
        isConversion,
        name: 'client-currency-label',
      }),
    [submissionCurrency, isConversion]
  );

  const displayBillingRateCurrencyLabel = useMemo(
    () =>
      OpportunitySubmissionCandidateRateUtil.getCurrencyLabel({
        currency: submissionCurrency,
        name: 'display-billing-rate-currency-label',
      }),
    [submissionCurrency]
  );

  if (!opportunityCandidate) {
    return null;
  }

  const {
    candidateBonusPercentage,
    exchangeRate,
    productBurdenRateMultiple,
    proposedMarginTarget,
    proposedHourlyRate,
  } = opportunityCandidate;
  const displayBillingRate =
    opportunityCandidate.displayBillingRate ?? 'hourly';

  const isDisabledState =
    OpportunitySubmissionCandidateRateUtil.isDisabledState({
      opportunityCandidate,
    });

  const cpqTargetsUnavailable =
    OpportunitySubmissionCandidateRateUtil.isCpqTargetsUnavailable({
      opportunityCandidate,
    });

  const defaultInputs = OpportunitySubmissionCandidateRateUtil.getDefaultInputs(
    {
      isConversion,
      opportunityCandidate,
    }
  );

  const talentHourlyComp =
    OpportunitySubmissionCandidateRateUtil.getTalentHourlyComp({
      opportunityCandidate,
    });

  const initialValues: Partial<OpportunitySubmissionCandidateRateSchemaType> =
    CandidateRateUtil.hasSubmittedRate(opportunityCandidate)
      ? {
          displayBillingRate,
          fieldToCalculate: fieldToCalculateRef.current,
          proposedHourlyComp:
            OpportunitySubmissionCandidateRateUtil.getProposedHourlyComp({
              isConversion,
              opportunityCandidate,
            }),
          proposedMargin: proposedMarginTarget,
          proposedHourlyBillRate: proposedHourlyRate ?? 0,
        }
      : {
          ...defaultInputs,
        };

  const disableBillingRateRadios =
    readOnly || isDisabledState || cpqTargetsUnavailable;

  return (
    <Form
      name={name}
      schema={OpportunitySubmissionCandidateRateSchema}
      initialValues={initialValues}
      onSubmit={async (_changedData, _actions, values) => {
        fieldToCalculateRef.current = values.fieldToCalculate;

        const {
          proposedHourlyComp,
          proposedMargin: patchProposedMarginTarget,
          proposedHourlyBillRate: patchProposedHourlyRate,
          displayBillingRate: patchDisplayBillRate,
        } = values;

        const patchProposedHourlyCompensation = isConversion
          ? OpportunitySubmissionCandidateRateUtil.getConvertedProposedHourlyCompensation(
              {
                exchangeRate,
                proposedHourlyComp,
              }
            )
          : proposedHourlyComp;

        const compType = CandidateOpportunityUtils.getCompensationType(
          {
            ...opportunityCandidate,
            proposedHourlyCompensation: values.proposedHourlyComp,
          },
          opportunityCandidate.position
        );

        try {
          await OpportunityLegacyApi.updateOpportunityCandidate(
            opportunityId,
            candidateId,
            {
              compensationType:
                compType === 'Awaiting Information' ? undefined : compType,
              displayBillingRate: patchDisplayBillRate,
              proposedHourlyCompensation: patchProposedHourlyCompensation,
              proposedHourlyCompCandidateCurrency: proposedHourlyComp,
              proposedHourlyRate: patchProposedHourlyRate,
              proposedMarginTarget: patchProposedMarginTarget,
            }
          );

          await refreshOpportunityCandidates(opportunityId);

          await OpportunityStore.load(opportunityId);
        } catch (error) {
          if (error instanceof BadRequestError) {
            ToastUtil.add({
              dismissible: true,
              name: 'set-rate-error',
              type: 'error',
              children: (
                <>
                  <CondensedHeader name="set-rate-error-header">
                    Unable to set rate
                  </CondensedHeader>
                  <CondensedLarge name="set-rate-error-message">
                    Check your inputs and try again.
                  </CondensedLarge>
                </>
              ),
            });
          }
        }
      }}
    >
      {({ values, setValues, fireSubmit }) => {
        const convertedComp =
          values.proposedHourlyComp && opportunityCandidate.exchangeRate
            ? (NumberUtil.formatNumberAsDecimal(
                OpportunitySubmissionCandidateRateUtil.getConvertedProposedHourlyCompensation(
                  {
                    proposedHourlyComp: values.proposedHourlyComp,
                    exchangeRate,
                  }
                )
              ) ?? '-')
            : '-';

        const disableSetRateButton =
          isDisabledState ||
          !isNumber(values.proposedHourlyComp) ||
          !isNumber(values.proposedHourlyBillRate) ||
          !isNumber(values.proposedMargin);
        return (
          <Gutter top="8px">
            {isConversion ? (
              <Gutter bottom="8px">
                <Flashy color="textAlert">
                  <CondensedMedium name="currency-mismatch-banner">
                    Talent compensation and client rate currencies don’t match!
                    Please don’t perform currency conversions yourself.
                  </CondensedMedium>
                </Flashy>
              </Gutter>
            ) : null}
            <Layout>
              <Layout>
                <LayoutItem>
                  <CondensedLabel>Talent hourly comp</CondensedLabel>
                  {talentHourlyComp ? (
                    <CondensedMedium name="talent-hourly-comp">
                      {NumberUtil.formatNumberAsDecimal(talentHourlyComp)}{' '}
                      {candidateCurrencyLabel}
                    </CondensedMedium>
                  ) : (
                    <CondensedMedium name="talent-hourly-comp">
                      Unavailable
                    </CondensedMedium>
                  )}
                </LayoutItem>
                <LayoutItem leftGutter="4px">
                  <Tooltip name="talent-hourly-comp-tooltip">
                    {OpportunitySubmissionCandidateRateUtil.getTalentHourlyCompTooltipCopy(
                      {
                        candidateCurrency,
                        opportunityCandidate,
                        hourlyComp: talentHourlyComp,
                      }
                    )}
                  </Tooltip>
                </LayoutItem>
              </Layout>
              <Layout>
                <LayoutItem>
                  <CondensedLabel>Comp type</CondensedLabel>
                  <CondensedMedium name="comp-type-value">
                    {CandidateOpportunityUtils.getCompensationType(
                      {
                        ...opportunityCandidate,
                        proposedHourlyCompensation: values.proposedHourlyComp,
                      },
                      opportunityCandidate.position
                    )}
                  </CondensedMedium>
                </LayoutItem>
              </Layout>
              {readOnly ? null : (
                <Layout position="right">
                  <LayoutItem>
                    <Button
                      name="get-talent-comp"
                      pattern="secondary"
                      onClick={async () => {
                        try {
                          await OpportunityLegacyApi.updateBurdendedCost(
                            opportunityId,
                            candidateId
                          );

                          await refreshOpportunityCandidates(opportunityId);

                          const oppCandResponse: OpportunityCandidate[] =
                            await getOpportunityCandidates(opportunityId);

                          const updatedOpportunityCandidate =
                            oppCandResponse.find(
                              oc => oc.candidateId === candidateId
                            );

                          if (updatedOpportunityCandidate) {
                            const isConversionPostUpdate =
                              OpportunitySubmissionCandidateRateUtil.isConversion(
                                {
                                  submissionCurrency,
                                  candidateCurrency,
                                }
                              );
                            const defaults =
                              OpportunitySubmissionCandidateRateUtil.getDefaultInputs(
                                {
                                  isConversion: isConversionPostUpdate,
                                  opportunityCandidate:
                                    updatedOpportunityCandidate,
                                }
                              );

                            await setValues({
                              ...values,
                              displayBillingRate:
                                updatedOpportunityCandidate.displayBillingRate ??
                                'hourly',
                              proposedHourlyComp:
                                OpportunitySubmissionCandidateRateUtil.getProposedHourlyComp(
                                  {
                                    isConversion: isConversionPostUpdate,
                                    opportunityCandidate:
                                      updatedOpportunityCandidate,
                                  }
                                ),
                              proposedMargin:
                                updatedOpportunityCandidate.proposedMarginTarget ??
                                defaults.proposedMargin,
                              proposedHourlyBillRate:
                                updatedOpportunityCandidate.proposedHourlyRate ??
                                defaults.proposedHourlyBillRate,
                            });
                          }
                        } catch (e) {
                          PreloadedAppErrorsStore.show(
                            (
                              (e as ApiError).getErrors() as
                                { message: string } | undefined
                            )?.message
                          );
                        }
                      }}
                    >
                      GET TALENT COMP
                    </Button>
                  </LayoutItem>
                </Layout>
              )}
            </Layout>
            <Gutter vertical="8px">
              <Well background="light blue">
                {readOnly ? null : (
                  <CondensedLabel name="select-field-to-calculate">
                    Select a field to calculate:
                  </CondensedLabel>
                )}
                <Gutter top="4px">
                  <FormGroup>
                    {isConversion ? (
                      <Grid>
                        <GridRow columns={3}>
                          <GridColumn width={6}>
                            <OpportunitySubmissionCandidateRateInput
                              opportunityCandidate={opportunityCandidate}
                              fieldName="proposedHourlyComp"
                              fieldToCalculate={
                                values.fieldToCalculate ??
                                'proposedHourlyBillRate'
                              }
                              label={
                                <span data-test="proposed-hourly-comp-input-label">
                                  Proposed hourly comp {candidateCurrencyLabel}
                                </span>
                              }
                              onChange={async val => {
                                await setValues(currentValues => ({
                                  ...OpportunitySubmissionCandidateRateUtil.setCalculatedField(
                                    {
                                      values: {
                                        ...currentValues,
                                        proposedHourlyComp:
                                          (val as number) || 0,
                                      },
                                      exchangeRate,
                                      productBurdenRateMultiple,
                                      candidateBonusPercentage,
                                    }
                                  ),
                                }));
                              }}
                              readOnly={readOnly}
                              tooltip={OpportunitySubmissionCandidateRateUtil.getProposedHourlyCompTooltipCopy(
                                {
                                  candidateCurrency,
                                  opportunityCandidate,
                                  proposedHourlyComp: values.proposedHourlyComp,
                                  submissionCurrency,
                                }
                              )}
                            />
                          </GridColumn>
                          <OpportunitySubmissionCandidateRateCurrencyConversion
                            clientCurrencyLabel={clientCurrencyLabel}
                            convertedComp={convertedComp}
                            tooltipContent={OpportunitySubmissionCandidateRateUtil.getConvertedToCurrencyTooltip(
                              {
                                candidateCurrency,
                                convertedComp,
                                exchangeRate: exchangeRate ?? 0,
                                proposedHourlyComp:
                                  values.proposedHourlyComp ?? 0,
                                submissionCurrency,
                              }
                            )}
                          />
                        </GridRow>
                        <GridRow columns={3} gutterTop="4px">
                          <GridColumn width={6}>
                            <OpportunitySubmissionCandidateRateInput
                              opportunityCandidate={opportunityCandidate}
                              fieldName="proposedMargin"
                              fieldToCalculate={
                                values.fieldToCalculate ??
                                'proposedHourlyBillRate'
                              }
                              label="Proposed margin (%)"
                              onChange={async val => {
                                await setValues(currentValues => ({
                                  ...OpportunitySubmissionCandidateRateUtil.setCalculatedField(
                                    {
                                      values: {
                                        ...currentValues,
                                        proposedMargin: (val as number) || 0,
                                      },
                                      exchangeRate,
                                      productBurdenRateMultiple,
                                      candidateBonusPercentage,
                                    }
                                  ),
                                }));
                              }}
                              readOnly={readOnly}
                            />
                            <Gutter top="4px">
                              <OpportunitySubmissionCandidateRateInput
                                opportunityCandidate={opportunityCandidate}
                                fieldName="proposedHourlyBillRate"
                                fieldToCalculate={
                                  values.fieldToCalculate ??
                                  'proposedHourlyBillRate'
                                }
                                label={
                                  <span>
                                    Proposed hourly bill rate{' '}
                                    {clientCurrencyLabel}
                                  </span>
                                }
                                onChange={async val => {
                                  await setValues(currentValues => ({
                                    ...OpportunitySubmissionCandidateRateUtil.setCalculatedField(
                                      {
                                        values: {
                                          ...currentValues,
                                          proposedHourlyBillRate:
                                            (val as number) || 0,
                                        },
                                        exchangeRate,
                                        productBurdenRateMultiple,
                                        candidateBonusPercentage,
                                      }
                                    ),
                                  }));
                                }}
                                readOnly={readOnly}
                              />
                            </Gutter>
                          </GridColumn>
                          <GridColumn width={6}>
                            <OpportunitySubmissionCandidateRateCpqTargets
                              opportunityCandidate={opportunityCandidate}
                              onReset={() => {
                                void setValues(currentValues => ({
                                  ...currentValues,
                                  ...OpportunitySubmissionCandidateRateUtil.getDefaultInputs(
                                    {
                                      isConversion,
                                      opportunityCandidate: {
                                        ...opportunityCandidate,
                                        proposedHourlyCompCandidateCurrency:
                                          null,
                                      },
                                    }
                                  ),
                                }));
                              }}
                              readOnly={readOnly}
                              resetDisabled={OpportunitySubmissionCandidateRateUtil.isResetDisabled(
                                {
                                  formValues: values,
                                  opportunityCandidate,
                                }
                              )}
                              values={values}
                            />
                          </GridColumn>
                        </GridRow>
                      </Grid>
                    ) : (
                      <Grid>
                        <GridRow columns={2}>
                          <GridColumn width={6}>
                            <Layout direction="vertical">
                              <OpportunitySubmissionCandidateRateInput
                                opportunityCandidate={opportunityCandidate}
                                fieldName="proposedHourlyComp"
                                fieldToCalculate={
                                  values.fieldToCalculate ??
                                  'proposedHourlyBillRate'
                                }
                                label={
                                  <span data-test="proposed-hourly-comp-input-label">
                                    Proposed hourly comp{' '}
                                    {candidateCurrencyLabel}
                                  </span>
                                }
                                onChange={async val => {
                                  await setValues(currentValues => ({
                                    ...OpportunitySubmissionCandidateRateUtil.setCalculatedField(
                                      {
                                        values: {
                                          ...currentValues,
                                          proposedHourlyComp:
                                            (val as number) || 0,
                                        },
                                        exchangeRate,
                                        productBurdenRateMultiple,
                                        candidateBonusPercentage,
                                      }
                                    ),
                                  }));
                                }}
                                readOnly={readOnly}
                                tooltip={OpportunitySubmissionCandidateRateUtil.getProposedHourlyCompTooltipCopy(
                                  {
                                    candidateCurrency,
                                    opportunityCandidate,
                                    proposedHourlyComp:
                                      values.proposedHourlyComp,
                                    submissionCurrency,
                                  }
                                )}
                              />
                              <Gutter top="4px">
                                <OpportunitySubmissionCandidateRateInput
                                  opportunityCandidate={opportunityCandidate}
                                  fieldName="proposedMargin"
                                  fieldToCalculate={
                                    values.fieldToCalculate ??
                                    'proposedHourlyBillRate'
                                  }
                                  label="Proposed margin (%)"
                                  onChange={async val => {
                                    await setValues(currentValues => ({
                                      ...OpportunitySubmissionCandidateRateUtil.setCalculatedField(
                                        {
                                          values: {
                                            ...currentValues,
                                            proposedMargin:
                                              (val as number) || 0,
                                          },
                                          exchangeRate,
                                          productBurdenRateMultiple,
                                          candidateBonusPercentage,
                                        }
                                      ),
                                    }));
                                  }}
                                  readOnly={readOnly}
                                />
                              </Gutter>
                              <Gutter top="4px">
                                <OpportunitySubmissionCandidateRateInput
                                  opportunityCandidate={opportunityCandidate}
                                  fieldName="proposedHourlyBillRate"
                                  fieldToCalculate={
                                    values.fieldToCalculate ??
                                    'proposedHourlyBillRate'
                                  }
                                  label={
                                    <span>
                                      Proposed hourly bill rate{' '}
                                      {clientCurrencyLabel}
                                    </span>
                                  }
                                  onChange={async val => {
                                    await setValues(currentValues => ({
                                      ...OpportunitySubmissionCandidateRateUtil.setCalculatedField(
                                        {
                                          values: {
                                            ...currentValues,
                                            proposedHourlyBillRate:
                                              (val as number) || 0,
                                          },
                                          exchangeRate,
                                          productBurdenRateMultiple,
                                          candidateBonusPercentage,
                                        }
                                      ),
                                    }));
                                  }}
                                  readOnly={readOnly}
                                />
                              </Gutter>
                            </Layout>
                          </GridColumn>
                          <GridColumn width={6}>
                            <OpportunitySubmissionCandidateRateCpqTargets
                              opportunityCandidate={opportunityCandidate}
                              onReset={() => {
                                void setValues(currentValues => ({
                                  ...currentValues,
                                  ...OpportunitySubmissionCandidateRateUtil.getDefaultInputs(
                                    {
                                      isConversion,
                                      opportunityCandidate: {
                                        ...opportunityCandidate,
                                        proposedHourlyCompCandidateCurrency:
                                          null,
                                      },
                                    }
                                  ),
                                }));
                              }}
                              readOnly={readOnly}
                              resetDisabled={OpportunitySubmissionCandidateRateUtil.isResetDisabled(
                                {
                                  formValues: values,
                                  opportunityCandidate,
                                }
                              )}
                              values={values}
                            />
                          </GridColumn>
                        </GridRow>
                      </Grid>
                    )}
                  </FormGroup>
                </Gutter>
              </Well>
            </Gutter>
            <Gutter>
              <CondensedMedium>
                Select how you want to display the talent rate to the client.
                Note whether the rate is within CPQ guidelines - if it isn’t,
                approval will be required prior to contracting.
              </CondensedMedium>
              <Gutter top="8px">
                <FormGroup>
                  <Gutter bottom="8px">
                    <Radio
                      disabled={disableBillingRateRadios}
                      name="displayBillingRate"
                      option="hourly"
                      displayValue={
                        <CondensedMedium name="hourly-rate-radio-label">
                          <Flashy bold>Hourly rate:</Flashy>{' '}
                          {OpportunitySubmissionCandidateRateUtil.formatBillingRate(
                            {
                              rate: values.proposedHourlyBillRate,
                            }
                          )}{' '}
                          <Flashy bold>
                            {displayBillingRateCurrencyLabel}
                          </Flashy>
                        </CondensedMedium>
                      }
                    />
                  </Gutter>
                  <Gutter bottom="8px">
                    <Radio
                      disabled={disableBillingRateRadios}
                      name="displayBillingRate"
                      option="daily"
                      displayValue={
                        <CondensedMedium name="daily-rate-radio-label">
                          <Flashy bold>Daily rate:</Flashy>{' '}
                          {OpportunitySubmissionCandidateRateUtil.formatBillingRateWithHours(
                            {
                              rate: values.proposedHourlyBillRate,
                              hoursPerDay: candidate.hoursPerDay,
                            }
                          )}{' '}
                          (Hourly x {candidate.hoursPerDay ?? 0}){' '}
                          <Flashy bold>
                            {displayBillingRateCurrencyLabel}
                          </Flashy>{' '}
                          - Hours per day are based on talent geography (not
                          client geography)
                        </CondensedMedium>
                      }
                    />
                  </Gutter>
                  <Gutter bottom="8px">
                    <Radio
                      disabled={disableBillingRateRadios}
                      name="displayBillingRate"
                      option="weekly"
                      displayValue={
                        <CondensedMedium name="weekly-rate-radio-label">
                          <Flashy bold>Weekly rate:</Flashy>{' '}
                          {candidate.hoursPerDay === null
                            ? OpportunitySubmissionCandidateRateUtil.formatBillingRateWithHours(
                                {
                                  rate: values.proposedHourlyBillRate,
                                  hoursPerDay: candidate.hoursPerDay,
                                }
                              )
                            : OpportunitySubmissionCandidateRateUtil.formatBillingRateWithHours(
                                {
                                  rate: values.proposedHourlyBillRate,
                                  hoursPerDay: candidate.hoursPerDay * 5,
                                }
                              )}{' '}
                          (Daily Rate x 5){' '}
                          <Flashy bold>
                            {displayBillingRateCurrencyLabel}
                          </Flashy>
                        </CondensedMedium>
                      }
                    />
                  </Gutter>
                  <Gutter bottom="8px">
                    <Radio
                      disabled={disableBillingRateRadios}
                      name="displayBillingRate"
                      option="none"
                      displayValue={
                        <CondensedMedium name="dont-display-radio-label">
                          <Flashy bold>Don't display</Flashy>
                        </CondensedMedium>
                      }
                    />
                  </Gutter>
                </FormGroup>
              </Gutter>
              {readOnly || cpqTargetsUnavailable ? null : (
                <Layout position="right">
                  <Button
                    name="set-rate-button"
                    disabled={disableSetRateButton}
                    onClick={fireSubmit}
                  >
                    SET RATE
                  </Button>
                </Layout>
              )}
            </Gutter>
          </Gutter>
        );
      }}
    </Form>
  );
};
